import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiUpload, FiEye, FiEdit2, FiTrash2, FiFilePlus, FiFilter } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, DataTable, Button, Badge, Toggle, ConfirmDialog } from '../../components/common';
import ClientFormModal from './components/ClientFormModal';
import { billingClientService } from '../../services/billingClientService';
import { getApiError } from '../../services/cmsService';
import { useDebounce } from '../../hooks';
import { formatINR } from './billingUtils';
import './Billing.css';

const PER_PAGE = 15;

const BillingClientsPage = () => {
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [hasPending, setHasPending] = useState('');
  const debouncedSearch = useDebounce(search, 350);

  const [formTarget, setFormTarget] = useState(null); // {} for create, a client for edit
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { page, per_page: PER_PAGE };
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    if (status !== '') params.status = status;
    if (hasPending !== '') params.has_pending = hasPending;

    billingClientService
      .getAll(params)
      .then((res) => {
        if (cancelled) return;
        setClients(res.items);
        setTotal(res.total);
      })
      .catch((error) => {
        if (!cancelled) toast.error(getApiError(error, 'Failed to load clients'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, status, hasPending, reloadKey]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, status, hasPending]);

  const reload = () => setReloadKey((k) => k + 1);

  const handleToggle = async (row) => {
    const previous = clients;
    setClients((prev) => prev.map((c) => (c.id === row.id ? { ...c, isActive: !c.isActive } : c)));
    try {
      await billingClientService.toggleStatus(row.id);
    } catch (error) {
      setClients(previous);
      toast.error(getApiError(error, 'Failed to update status'));
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await billingClientService.delete(deleteTarget.id);
      toast.success('Client deleted');
      reload();
    } catch (error) {
      // 422 when the client still has unpaid issued invoices
      toast.error(getApiError(error, 'Failed to delete the client'));
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { header: 'Client ID', width: '120px', render: (row) => <span className="bl-mono">{row.clientCode || '—'}</span> },
    {
      header: 'Name',
      render: (row) => (
        <button type="button" className="bl-link" onClick={() => navigate(`/billing/clients/${row.id}`)}>
          {row.name}
        </button>
      ),
    },
    { header: 'GSTIN', render: (row) => <span className="bl-mono">{row.gstin || <span className="bl-muted">—</span>}</span> },
    {
      header: 'State',
      render: (row) => (row.stateName ? row.stateName : <Badge variant="warning" size="small">Unknown</Badge>),
    },
    {
      header: 'Pending',
      align: 'center',
      render: (row) =>
        row.pendingProjectsCount > 0 ? <Badge variant="orange" size="small">{row.pendingProjectsCount}</Badge> : <span className="bl-muted">0</span>,
    },
    {
      header: 'Outstanding',
      align: 'right',
      render: (row) => <span className="bl-amount">{formatINR(row.outstandingAmount)}</span>,
    },
    {
      header: 'Status',
      width: '90px',
      render: (row) => (
        <Toggle name={`bc-status-${row.id}`} size="small" checked={row.isActive} onChange={() => handleToggle(row)} />
      ),
    },
    {
      header: 'Actions',
      width: '170px',
      align: 'center',
      render: (row) => (
        <div className="bl-actions">
          <button type="button" className="action-btn view" title="View" onClick={() => navigate(`/billing/clients/${row.id}`)}>
            <FiEye />
          </button>
          <button type="button" className="action-btn edit" title="Edit" onClick={() => setFormTarget(row)}>
            <FiEdit2 />
          </button>
          <button
            type="button"
            className="action-btn"
            title="Create invoice"
            onClick={() => navigate(`/billing/invoices/new?client_id=${row.id}`)}
          >
            <FiFilePlus />
          </button>
          <button type="button" className="action-btn delete" title="Delete" onClick={() => setDeleteTarget(row)}>
            <FiTrash2 />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="bl-page">
      <Card
        title="Billing Clients"
        subtitle={`${total} ${total === 1 ? 'client' : 'clients'} · the parties you raise tax invoices against`}
        action={
          <div className="bl-header-actions">
            <Button variant="outline" icon={<FiUpload />} onClick={() => navigate('/billing/clients/import')}>
              Import
            </Button>
            <Button icon={<FiPlus />} onClick={() => setFormTarget({})}>
              Add client
            </Button>
          </div>
        }
      >
        <DataTable
          columns={columns}
          data={clients}
          loading={loading}
          searchable
          searchPlaceholder="Search name, GSTIN or client ID…"
          searchValue={search}
          onSearch={setSearch}
          pagination
          pageSize={PER_PAGE}
          totalItems={total}
          currentPage={page}
          onPageChange={setPage}
          emptyMessage="No billing clients found."
          actions={
            <>
              <div className="bl-filter">
                <FiFilter />
                <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
                  <option value="">All statuses</option>
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>
              <div className="bl-filter">
                <select value={hasPending} onChange={(e) => setHasPending(e.target.value)} aria-label="Filter by pending projects">
                  <option value="">Any projects</option>
                  <option value="1">Has pending projects</option>
                  <option value="0">No pending projects</option>
                </select>
              </div>
            </>
          }
        />
      </Card>

      <ClientFormModal
        isOpen={Boolean(formTarget)}
        onClose={() => setFormTarget(null)}
        client={formTarget?.id ? formTarget : null}
        onSaved={(saved) => {
          if (formTarget?.id) reload();
          else navigate(`/billing/clients/${saved.id}`);
        }}
      />

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete client"
        message={`Delete "${deleteTarget?.name}"? Issued invoices keep their copy of the client details. Clients with unpaid issued invoices can't be deleted.`}
        confirmText="Delete client"
        loading={deleting}
      />
    </div>
  );
};

export default BillingClientsPage;
